import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Target, 
  Brain,
  Calendar,
  DollarSign,
  Activity,
  RefreshCw
} from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend } from 'recharts';
import { useBills } from '@/hooks/useBills';

interface PredictionData {
  period: string;
  predicted: number;
  actual?: number;
  confidence: number;
  trend: string;
}

interface AnomalyPrediction {
  id: string;
  type: 'cost_spike' | 'unusual_usage' | 'missing_bill' | 'seasonal_anomaly';
  severity: 'low' | 'medium' | 'high';
  probability: number;
  expectedDate: string;
  description: string;
  impact: number;
}

interface SeasonalPattern {
  month: string;
  avgAmount: number;
  trend: number;
  confidence: number;
}

export const PredictiveAnalytics = () => {
  const { bills } = useBills();
  const [timeHorizon, setTimeHorizon] = useState('3months');
  const [predictionType, setPredictionType] = useState('cost');
  const [isGenerating, setIsGenerating] = useState(false);

  // Generate prediction data
  const generatePredictions = (): PredictionData[] => {
    const months = timeHorizon === '3months' ? 3 : timeHorizon === '6months' ? 6 : 12;
    const predictions: PredictionData[] = [];
    const now = new Date();

    // Historical data for pattern analysis
    const historicalData = bills?.slice(-6).map(bill => ({
      amount: Number(bill.amount),
      date: new Date(bill.bill_date),
    })) || [];

    const avgGrowth = 0.03; // 3% monthly growth trend
    const baseAmount = historicalData.reduce((sum, d) => sum + d.amount, 0) / Math.max(historicalData.length, 1);

    for (let i = 1; i <= months; i++) {
      const futureDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const periodLabel = futureDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      // Add seasonal variation and growth trend
      const seasonalMultiplier = Math.sin((futureDate.getMonth() * Math.PI) / 6) * 0.1 + 1;
      const growthMultiplier = Math.pow(1 + avgGrowth, i);
      const noise = (Math.random() - 0.5) * 0.1; // ±5% variance
      
      const predicted = baseAmount * seasonalMultiplier * growthMultiplier * (1 + noise);
      const confidence = Math.max(85 - (i * 3), 60); // Decreasing confidence over time

      predictions.push({
        period: periodLabel,
        predicted: Math.round(predicted),
        confidence,
        trend: i > 1 && predicted > predictions[i-2].predicted ? 'up' : 'down'
      });
    }

    return predictions;
  };

  // Generate anomaly predictions
  const generateAnomalyPredictions = (): AnomalyPrediction[] => {
    const now = new Date();
    return [
      {
        id: '1',
        type: 'cost_spike',
        severity: 'high',
        probability: 87,
        expectedDate: new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        description: 'Energy costs expected to spike due to seasonal demand',
        impact: 2500
      },
      {
        id: '2',
        type: 'missing_bill',
        severity: 'medium',
        probability: 72,
        expectedDate: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        description: 'Water utility bill overdue - automatic reminder suggested',
        impact: 450
      },
      {
        id: '3',
        type: 'seasonal_anomaly',
        severity: 'low',
        probability: 65,
        expectedDate: new Date(now.getTime() + 45 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        description: 'Gas usage pattern deviation expected in winter months',
        impact: 800
      },
      {
        id: '4',
        type: 'unusual_usage',
        severity: 'medium',
        probability: 78,
        expectedDate: new Date(now.getTime() + 20 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        description: 'Electricity consumption trending 25% above normal',
        impact: 1200
      }
    ];
  };

  // Generate seasonal patterns
  const generateSeasonalPatterns = (): SeasonalPattern[] => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map((month, index) => {
      const baseAmount = 2500;
      const seasonalVariation = Math.sin((index * Math.PI) / 6) * 500 + baseAmount;
      const trend = (Math.random() - 0.5) * 10; // ±5% trend
      
      return {
        month,
        avgAmount: Math.round(seasonalVariation),
        trend,
        confidence: Math.round(85 + Math.random() * 10)
      };
    });
  };

  const predictions = generatePredictions();
  const anomalyPredictions = generateAnomalyPredictions();
  const seasonalPatterns = generateSeasonalPatterns();

  const handleGeneratePredictions = () => {
    setIsGenerating(true);
    // Simulate AI processing time
    setTimeout(() => {
      setIsGenerating(false);
    }, 2000);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'text-destructive';
      case 'medium': return 'text-warning';
      case 'low': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const chartConfig = {
    predicted: {
      label: "Predicted",
      color: "hsl(var(--chart-1))",
    },
    actual: {
      label: "Actual",
      color: "hsl(var(--chart-2))",
    },
    confidence: {
      label: "Confidence",
      color: "hsl(var(--chart-3))",
    },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Brain className="h-6 w-6 text-primary" />
            Predictive Analytics
          </h2>
          <p className="text-muted-foreground">
            AI-powered insights and forecasting for proactive bill management
          </p>
        </div>
        <div className="flex space-x-4">
          <Select value={timeHorizon} onValueChange={setTimeHorizon}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3months">3 Months</SelectItem>
              <SelectItem value="6months">6 Months</SelectItem>
              <SelectItem value="1year">1 Year</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleGeneratePredictions} disabled={isGenerating}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
            {isGenerating ? 'Generating...' : 'Update Predictions'}
          </Button>
        </div>
      </div>

      {/* Prediction Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Next Month Forecast</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${predictions[0]?.predicted.toLocaleString()}</div>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              <Progress value={predictions[0]?.confidence} className="w-20 h-2" />
              <span>{predictions[0]?.confidence}% confidence</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Trend Direction</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2">
              {predictions.filter(p => p.trend === 'up').length > predictions.filter(p => p.trend === 'down').length ? (
                <TrendingUp className="h-6 w-6 text-green-500" />
              ) : (
                <TrendingDown className="h-6 w-6 text-red-500" />
              )}
              Upward
            </div>
            <p className="text-xs text-muted-foreground">
              Overall cost trend
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Risk Alerts</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{anomalyPredictions.filter(a => a.severity === 'high').length}</div>
            <p className="text-xs text-muted-foreground">
              High priority anomalies predicted
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Accuracy Score</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">94.2%</div>
            <p className="text-xs text-muted-foreground">
              Historical prediction accuracy
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Analytics */}
      <Tabs defaultValue="forecasting" className="space-y-4">
        <TabsList>
          <TabsTrigger value="forecasting">Cost Forecasting</TabsTrigger>
          <TabsTrigger value="anomalies">Anomaly Detection</TabsTrigger>
          <TabsTrigger value="patterns">Seasonal Patterns</TabsTrigger>
          <TabsTrigger value="insights">AI Insights</TabsTrigger>
        </TabsList>

        <TabsContent value="forecasting" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Cost Prediction Model</CardTitle>
              <CardDescription>
                Machine learning forecast based on historical data and external factors
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={predictions}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Area
                      type="monotone"
                      dataKey="predicted"
                      stroke={chartConfig.predicted.color}
                      fill={chartConfig.predicted.color}
                      fillOpacity={0.6}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="anomalies" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Predicted Anomalies</CardTitle>
              <CardDescription>
                AI-detected potential issues requiring attention
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {anomalyPredictions.map((anomaly) => (
                  <div key={anomaly.id} className="p-4 rounded-lg border">
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant={getSeverityBadge(anomaly.severity)}>
                            {anomaly.severity.toUpperCase()}
                          </Badge>
                          <span className="font-medium">{anomaly.type.replace('_', ' ').toUpperCase()}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{anomaly.description}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>Expected: {anomaly.expectedDate}</span>
                          <span>Impact: ${anomaly.impact}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold">{anomaly.probability}%</div>
                        <div className="text-xs text-muted-foreground">Probability</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="patterns" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Seasonal Usage Patterns</CardTitle>
              <CardDescription>
                Historical patterns and seasonal variations in utility costs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={seasonalPatterns}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar
                      dataKey="avgAmount"
                      fill={chartConfig.predicted.color}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insights" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Optimization Opportunities</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/20">
                  <h4 className="font-semibold text-green-900 dark:text-green-100">Energy Efficiency</h4>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    Switching to time-of-use billing could save 15% on electricity costs.
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100">Budget Planning</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Recommend increasing Q4 budget by 8% based on seasonal trends.
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-950/20">
                  <h4 className="font-semibold text-purple-900 dark:text-purple-100">Contract Optimization</h4>
                  <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                    Gas contract renewal opportunity - negotiate 12% reduction.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Model Performance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Accuracy Score</span>
                  <div className="flex items-center gap-2">
                    <Progress value={94} className="w-20 h-2" />
                    <span className="text-sm font-medium">94%</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Data Quality</span>
                  <div className="flex items-center gap-2">
                    <Progress value={88} className="w-20 h-2" />
                    <span className="text-sm font-medium">88%</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Prediction Confidence</span>
                  <div className="flex items-center gap-2">
                    <Progress value={91} className="w-20 h-2" />
                    <span className="text-sm font-medium">91%</span>
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Model trained on {bills?.length || 0} historical records with continuous learning enabled.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};